
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { showErrorToast } from "@/utils/errorHandling";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Download, Check, Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { IndeterminateCheckbox } from "./import/IndeterminateCheckbox";
import type { ImportDataType } from "./types";

export function DataExport() {
  const [activeDataType, setActiveDataType] = useState<ImportDataType>("clients");
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});

  // Get available fields for the selected data type
  const getAvailableFields = () => {
    if (activeDataType === "clients") {
      return {
        name: true,
        industry: true,
        website: true,
        contact_email: true,
        contact_phone: true,
        address: true,
        notes: true,
        status: true,
        created_at: true,
        updated_at: false
      };
    } 
    return {
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
      source: true,
      status: true,
      linkedin_url: true,
      experience_years: true,
      notes: true,
      skills: true,
      created_at: true,
      updated_at: false,
      resume_url: false
    };
  };

  // Initialize selected fields when data type changes
  const initializeFields = () => {
    setSelectedFields(getAvailableFields());
  };

  // Handle field selection change
  const handleFieldToggle = (field: string) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Handle select all fields
  const handleSelectAll = () => {
    const fields = getAvailableFields();
    const allSelected = Object.values(selectedFields).every(value => value);
    
    const newSelection = Object.keys(fields).reduce((acc, field) => {
      acc[field] = !allSelected;
      return acc;
    }, {} as Record<string, boolean>);
    
    setSelectedFields(newSelection);
  };

  // Export data as CSV
  const handleExport = async () => {
    setIsExporting(true);
    setExportSuccess(false);
    
    try {
      // Get selected fields as a string for the query
      const fields = Object.entries(selectedFields)
        .filter(([_, selected]) => selected)
        .map(([field, _]) => field)
        .join(',');
      
      if (!fields) {
        throw new Error("Please select at least one field to export");
      }
      
      // Fetch data from Supabase
      const { data, error } = await supabase
        .from(activeDataType)
        .select(fields);
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error(`No ${activeDataType} found to export`);
      }
      
      // Convert data to CSV
      const headers = Object.entries(selectedFields)
        .filter(([_, selected]) => selected)
        .map(([field, _]) => field)
        .join(',');
      
      const rows = data.map(row => 
        Object.entries(selectedFields)
          .filter(([_, selected]) => selected)
          .map(([field, _]) => {
            // Handle different data types for CSV export
            if (row[field] === null || row[field] === undefined) return '""';
            if (Array.isArray(row[field])) return `"${row[field].join(', ')}"`;
            return `"${String(row[field]).replace(/"/g, '""')}"`;
          })
          .join(',')
      ).join('\n');
      
      const csvContent = `${headers}\n${rows}`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeDataType}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportSuccess(true);
    } catch (error) {
      showErrorToast(error, `Failed to export ${activeDataType}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Initialize fields when tab changes
  const handleTabChange = (value: string) => {
    setActiveDataType(value as ImportDataType);
    setExportSuccess(false);
    initializeFields();
  };

  // Initialize on first load
  useState(() => {
    initializeFields();
  });

  return (
    <div className="space-y-6">
      <Tabs 
        value={activeDataType} 
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="candidates">Candidates</TabsTrigger>
        </TabsList>

        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Export Guide</AlertTitle>
          <AlertDescription>
            Select the fields you want to include in your export, then click Export to download a CSV file.
          </AlertDescription>
        </Alert>

        <TabsContent value="clients" className="space-y-6">
          <FieldSelector 
            fields={selectedFields}
            dataType="clients"
            onToggle={handleFieldToggle}
            onSelectAll={handleSelectAll}
          />
          
          <ExportActions 
            onExport={handleExport}
            isExporting={isExporting}
            success={exportSuccess}
            dataType="clients"
          />
        </TabsContent>

        <TabsContent value="candidates" className="space-y-6">
          <FieldSelector 
            fields={selectedFields}
            dataType="candidates"
            onToggle={handleFieldToggle}
            onSelectAll={handleSelectAll}
          />
          
          <ExportActions 
            onExport={handleExport}
            isExporting={isExporting}
            success={exportSuccess}
            dataType="candidates"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Field Selector Component
interface FieldSelectorProps {
  fields: Record<string, boolean>;
  dataType: ImportDataType;
  onToggle: (field: string) => void;
  onSelectAll: () => void;
}

function FieldSelector({ fields, dataType, onToggle, onSelectAll }: FieldSelectorProps) {
  const allSelected = Object.values(fields).every(value => value);
  const someSelected = Object.values(fields).some(value => value);
  const isIndeterminate = someSelected && !allSelected;
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <IndeterminateCheckbox 
              id="select-all" 
              checked={allSelected} 
              indeterminate={isIndeterminate}
              onCheckedChange={onSelectAll}
            />
            <Label htmlFor="select-all" className="font-medium">
              {allSelected ? "Deselect All Fields" : "Select All Fields"}
            </Label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(fields).map(([field, checked]) => (
              <div key={field} className="flex items-center space-x-2">
                <Checkbox 
                  id={field} 
                  checked={checked} 
                  onCheckedChange={() => onToggle(field)}
                />
                <Label htmlFor={field} className="capitalize">
                  {field.replace(/_/g, ' ')}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Export Actions Component
interface ExportActionsProps {
  onExport: () => Promise<void>;
  isExporting: boolean;
  success: boolean;
  dataType: ImportDataType;
}

function ExportActions({ onExport, isExporting, success, dataType }: ExportActionsProps) {
  return (
    <div className="space-y-4">
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Export Successful</AlertTitle>
          <AlertDescription className="text-green-700">
            Your {dataType} data has been exported successfully.
          </AlertDescription>
        </Alert>
      )}
      
      <Button 
        onClick={onExport} 
        disabled={isExporting}
        className="w-full sm:w-auto flex items-center gap-2"
      >
        <Download className="h-4 w-4" />
        {isExporting ? "Exporting..." : `Export ${dataType}`}
      </Button>
    </div>
  );
}
